import { memo, useCallback, useMemo, useState } from "react";

import HandshakeIcon from "@mui/icons-material/Handshake";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

import TextSnippetIcon from "@mui/icons-material/TextSnippet";
import {
  Box,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";

import StatusCircle from "../../../../common/components/badge/StatusCircle";
import { useHasRoleAccess } from "../../../../common/hooks/useCheckRoleAccess";
import useLocationFromCache from "../../../../common/hooks/useLocationFromCache";
import { RoleEnum } from "../../../../common/models/enums";
import { IDeploymentHealthTableRow } from "../../hooks/useDeploymentHealthTableRows";
import { DialogState } from "../DeploymentHealthTable";

const MAX_DAYS_AGO = 90;
const EPOCH_2000_MS = 946684800000; // default year 2000 epoch

const FACILITY_REASONS = [
  "Onboarding",
  "Zone setup",
  "Site Power Issue",
  "Customer Network",
];

interface ITableHeadRowProps {
  row: IDeploymentHealthTableRow;
  onOpenDialog: (params: Omit<DialogState, "isOpen">) => void;
}

interface ComputedEntityItem {
  id: string;
  name: string;
  statusColor: string;
  showReasonIcon: boolean;
  reason: string;
  showWatcherIcon: boolean;
  watcherTooltip: string;
  showLastAlive: boolean;
  lastAliveLabel: string;
  // For dialog
  isExpectedOnline: boolean | undefined;
  leafPk: string | undefined;
  leafSk: string | undefined;
  assignedWatcher: string | undefined;
  isRed: boolean;
}

const formatLastAliveAge = (epochMs: number): string => {
  const now = Date.now();
  const diffMs = Math.max(now - epochMs, 0);
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins === 0) return "Just now";

  if (diffMins < 60) return `${diffMins}m ago`;

  const diffHours = Math.floor(diffMins / 60);

  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  const remainingHours = diffHours % 24;

  return diffDays >= MAX_DAYS_AGO
    ? `> ${MAX_DAYS_AGO}d ago`
    : `${diffDays}d${remainingHours > 0 ? ` ${remainingHours}h` : ""} ago`;
};

const getLastAliveLabel = (lastAliveEpochMs?: number): string => {
  if (
    lastAliveEpochMs == null ||
    !Number.isFinite(lastAliveEpochMs) ||
    lastAliveEpochMs <= EPOCH_2000_MS
  ) {
    return "N/A";
  }

  return formatLastAliveAge(lastAliveEpochMs);
};

const getWatcherTooltipText = (assignedWatcher: string | undefined): string => {
  switch (assignedWatcher) {
    case "CUSTOMER":
      return "Customer";
    case "THIRD_PARTY":
      return "Third Party";
    default:
      return "Other";
  }
};

// Memoized entity item component to prevent re-renders
interface EntityItemProps {
  item: ComputedEntityItem;
  entityType: "node" | "device" | "model";
  locationId: string;
  canEdit: boolean;
  onOpenDialog: (params: Omit<DialogState, "isOpen">) => void;
  textColor: string;
  hoverColor: string;
  secondaryTextColor: string;
  infoColor: string;
}

const EntityItem = memo(
  ({
    item,
    entityType,
    locationId,
    canEdit,
    onOpenDialog,
    textColor,
    hoverColor,
    secondaryTextColor,
    infoColor,
  }: EntityItemProps): JSX.Element => {
    const handleClick = useCallback(() => {
      onOpenDialog({
        targetLabel: item.name,
        entityType,
        entityId: item.id,
        locationId,
        initialIsExpectedOnline: item.isExpectedOnline,
        leafPk: item.leafPk,
        leafSk: item.leafSk,
        existingReason:
          typeof item.reason === "string" && item.reason.trim().length > 0
            ? item.reason
            : undefined,
        allowErrorReason: item.isRed,
        existingAssignedWatcher: item.assignedWatcher,
      });
    }, [
      onOpenDialog,
      item.name,
      item.id,
      item.isExpectedOnline,
      item.leafPk,
      item.leafSk,
      item.reason,
      item.isRed,
      item.assignedWatcher,
      entityType,
      locationId,
    ]);

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
          <StatusCircle color={item.statusColor} />
          {item.showReasonIcon && (
            <Tooltip title={item.reason} enterDelay={300} enterNextDelay={300}>
              <TextSnippetIcon
                fontSize="small"
                sx={{ color: secondaryTextColor }}
              />
            </Tooltip>
          )}
          {item.showWatcherIcon && (
            <Tooltip
              title={item.watcherTooltip}
              enterDelay={300}
              enterNextDelay={300}
            >
              <HandshakeIcon fontSize="inherit" sx={{ color: infoColor }} />
            </Tooltip>
          )}
        </Box>
        {canEdit ? (
          <Typography
            component="button"
            variant="body2"
            onClick={handleClick}
            sx={{
              border: 0,
              background: "transparent",
              p: 0,
              m: 0,
              minWidth: "auto",
              color: textColor,
              cursor: "pointer",
              "&:hover": {
                color: hoverColor,
                backgroundColor: "transparent",
              },
            }}
          >
            {item.name}
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{
              border: 0,
              background: "transparent",
              p: 0,
              m: 0,
              minWidth: "auto",
              color: textColor,
            }}
          >
            {item.name}
          </Typography>
        )}
        {item.showLastAlive && (
          <Typography variant="caption" color="text.secondary">
            {item.lastAliveLabel}
          </Typography>
        )}
      </Box>
    );
  }
);

EntityItem.displayName = "EntityItem";

// Helper to compute status color - defined outside component for stability
const getEntityStatusColor = (
  isExpectedOnline: boolean | undefined,
  status: boolean,
  greenColor: string,
  redColor: string,
  greyColor: string
): string => {
  if (isExpectedOnline === false) return greyColor;

  return status ? greenColor : redColor;
};

const DeploymentHealthTableBodyRowInner = ({
  row,
  onOpenDialog,
}: ITableHeadRowProps): JSX.Element => {
  const canEditHealth = useHasRoleAccess([
    RoleEnum.ROOT,
    RoleEnum.CC_OPS_ADMIN,
    RoleEnum.CC_OPS,
    RoleEnum.CC_SALES,
  ]);

  const theme = useTheme();

  // Extract primitive color values (strings are stable)
  const greenColor = theme.palette.primary.main;
  const redColor = theme.palette.error.main;
  const greyColor = theme.palette.grey[500];
  const textColor = theme.palette.text.primary;
  const textSecondaryColor = theme.palette.text.secondary;
  const successColor = theme.palette.success.main;
  const infoColor = theme.palette.info.main;

  const { getCachedLocation } = useLocationFromCache();
  const locationCache = getCachedLocation(row.locationId);

  const [open, setOpen] = useState(!row.locationStatus);

  const locationLabel = useMemo(
    () => locationCache?.name ?? row.locationName ?? row.locationId,
    [locationCache?.name, row.locationName, row.locationId]
  );

  // Pre-compute all aggregate values once
  const aggregates = useMemo(() => {
    const nodeAllExpectedOffline = row.nodes.every(
      n => n.isExpectedOnline === false
    );
    const deviceAllExpectedOffline = row.devices.every(
      d => d.isExpectedOnline === false
    );
    const serviceAllExpectedOffline = row.services.every(
      s => s.isExpectedOnline === false
    );
    const locationAllExpectedOffline =
      nodeAllExpectedOffline &&
      deviceAllExpectedOffline &&
      serviceAllExpectedOffline;

    const fullyWatched =
      row.nodes.every(n => n.assignedWatcher === "CLEANCONNECT") &&
      row.devices.every(d => d.assignedWatcher === "CLEANCONNECT") &&
      row.services.every(s => s.assignedWatcher === "CLEANCONNECT");

    const childReasons = [
      ...row.nodes
        .filter(n => n.isExpectedOnline === false && !!n.reason)
        .map(n => n.reason as string),
      ...row.devices
        .filter(d => d.isExpectedOnline === false && !!d.reason)
        .map(d => d.reason as string),
      ...row.services
        .filter(s => s.isExpectedOnline === false && !!s.reason)
        .map(s => s.reason as string),
    ];
    const facilityReason = childReasons.find(r => FACILITY_REASONS.includes(r));
    const locationReason = facilityReason ?? childReasons[0] ?? "";

    return {
      nodeAllExpectedOffline,
      deviceAllExpectedOffline,
      serviceAllExpectedOffline,
      locationAllExpectedOffline,
      fullyWatched,
      locationReason,
      showLocationReason: locationReason.trim().length > 0,
    };
  }, [row.nodes, row.devices, row.services]);

  const locationDotColor = aggregates.locationAllExpectedOffline
    ? greyColor
    : row.locationStatus
    ? greenColor
    : redColor;

  // Pre-compute all node items
  const computedNodes = useMemo((): ComputedEntityItem[] => {
    return row.nodes.map(node => {
      const statusColor = getEntityStatusColor(
        node.isExpectedOnline,
        node.status,
        greenColor,
        redColor,
        greyColor
      );
      const reason = node.reason ?? "";
      const showReason = reason.trim().length > 0;
      const isNotOnline = statusColor !== greenColor;

      return {
        id: node.nodeId,
        name: node.nodeName,
        statusColor,
        showReasonIcon: isNotOnline && showReason,
        reason,
        showWatcherIcon: node.assignedWatcher !== "CLEANCONNECT",
        watcherTooltip: getWatcherTooltipText(node.assignedWatcher),
        showLastAlive: !node.status && !!node.lastAliveEpochMs,
        lastAliveLabel: getLastAliveLabel(node.lastAliveEpochMs),
        isExpectedOnline: node.isExpectedOnline,
        leafPk: node.leafPk,
        leafSk: node.leafSk,
        assignedWatcher: node.assignedWatcher,
        isRed: node.isExpectedOnline !== false && !node.status,
      };
    });
  }, [row.nodes]);

  // Pre-compute all device items
  const computedDevices = useMemo((): ComputedEntityItem[] => {
    return row.devices.map(device => {
      const statusColor = getEntityStatusColor(
        device.isExpectedOnline,
        device.status,
        greenColor,
        redColor,
        greyColor
      );
      const reason = device.reason ?? "";
      const showReason = reason.trim().length > 0;
      const isNotOnline = statusColor !== greenColor;

      return {
        id: device.deviceId,
        name: device.deviceName,
        statusColor,
        showReasonIcon: isNotOnline && showReason,
        reason,
        showWatcherIcon: device.assignedWatcher !== "CLEANCONNECT",
        watcherTooltip: getWatcherTooltipText(device.assignedWatcher),
        showLastAlive: !device.status && !!device.lastAliveEpochMs,
        lastAliveLabel: getLastAliveLabel(device.lastAliveEpochMs),
        isExpectedOnline: device.isExpectedOnline,
        leafPk: device.leafPk,
        leafSk: device.leafSk,
        assignedWatcher: device.assignedWatcher,
        isRed: device.isExpectedOnline !== false && !device.status,
      };
    });
  }, [row.devices]);

  // Pre-compute all service items
  const computedServices = useMemo((): ComputedEntityItem[] => {
    return row.services.map(service => {
      const statusColor = getEntityStatusColor(
        service.isExpectedOnline,
        service.status,
        greenColor,
        redColor,
        greyColor
      );
      const reason = service.reason ?? "";
      const showReason = reason.trim().length > 0;
      const isNotOnline = statusColor !== greenColor;

      return {
        id: service.serviceId,
        name: service.serviceLabel,
        statusColor,
        showReasonIcon: isNotOnline && showReason,
        reason,
        showWatcherIcon: service.assignedWatcher !== "CLEANCONNECT",
        watcherTooltip: getWatcherTooltipText(service.assignedWatcher),
        showLastAlive: !service.status && !!service.lastAliveEpochMs,
        lastAliveLabel: getLastAliveLabel(service.lastAliveEpochMs),
        isExpectedOnline: service.isExpectedOnline,
        leafPk: service.leafPk,
        leafSk: service.leafSk,
        assignedWatcher: service.assignedWatcher,
        isRed: service.isExpectedOnline !== false && !service.status,
      };
    });
  }, [row.services]);

  const handleToggleOpen = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  const handleLocationClick = useCallback(() => {
    onOpenDialog({
      targetLabel: locationLabel,
      entityType: "location",
      entityId: row.locationId,
      locationId: row.locationId,
      initialIsExpectedOnline: !aggregates.locationAllExpectedOffline,
      existingReason: undefined,
      allowErrorReason: false,
      existingAssignedWatcher: undefined,
      leafPk: undefined,
      leafSk: undefined,
    });
  }, [
    onOpenDialog,
    locationLabel,
    row.locationId,
    aggregates.locationAllExpectedOffline,
  ]);

  const clickableButtonSx = {
    border: 0,
    background: "transparent",
    p: 0,
    m: 0,
    minWidth: "auto",
    color: textColor,
    cursor: "pointer",
    "&:hover": {
      color: successColor,
      backgroundColor: "transparent",
    },
  };

  const nonClickableButtonSx = {
    border: 0,
    background: "transparent",
    p: 0,
    m: 0,
    minWidth: "auto",
    color: textColor,
  };

  const nodeStatusColor =
    aggregates.nodeAllExpectedOffline || row.nodes.length === 0
      ? greyColor
      : row.nodeStatus
      ? greenColor
      : redColor;

  const deviceStatusColor =
    aggregates.deviceAllExpectedOffline || row.devices.length === 0
      ? greyColor
      : row.deviceStatus
      ? greenColor
      : redColor;

  const serviceStatusColor =
    aggregates.serviceAllExpectedOffline || row.services.length === 0
      ? greyColor
      : row.serviceStatus
      ? greenColor
      : redColor;

  const collapsedRowSx = {
    visibility: open ? ("visible" as const) : ("collapse" as const),
  };

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton onClick={handleToggleOpen} size="small">
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <StatusCircle color={locationDotColor} />
            {locationDotColor === greyColor &&
              aggregates.showLocationReason && (
                <Tooltip
                  title={aggregates.locationReason}
                  enterDelay={300}
                  enterNextDelay={300}
                >
                  <TextSnippetIcon
                    fontSize="small"
                    sx={{ color: textSecondaryColor }}
                  />
                </Tooltip>
              )}
            {!aggregates.fullyWatched && (
              <Tooltip
                title="At least one node, device, or model is not managed by CleanConnect"
                enterDelay={300}
                enterNextDelay={300}
              >
                <HandshakeIcon fontSize="inherit" sx={{ color: infoColor }} />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>
          {canEditHealth ? (
            <Typography
              component="button"
              variant="body2"
              onClick={handleLocationClick}
              sx={clickableButtonSx}
            >
              {locationLabel}
            </Typography>
          ) : (
            <Typography variant="body2" sx={nonClickableButtonSx}>
              {locationLabel}
            </Typography>
          )}
        </TableCell>
        <TableCell>
          <StatusCircle color={nodeStatusColor} />
        </TableCell>
        <TableCell>
          <StatusCircle color={deviceStatusColor} />
        </TableCell>
        <TableCell>
          <StatusCircle color={serviceStatusColor} />
        </TableCell>
      </TableRow>

      <TableRow sx={collapsedRowSx}>
        <TableCell />
        <TableCell />
        <TableCell />
        <TableCell sx={{ verticalAlign: "top" }}>
          {computedNodes.map(item => (
            <EntityItem
              key={item.id}
              item={item}
              entityType="node"
              locationId={row.locationId}
              canEdit={canEditHealth}
              onOpenDialog={onOpenDialog}
              textColor={textColor}
              hoverColor={successColor}
              secondaryTextColor={textSecondaryColor}
              infoColor={infoColor}
            />
          ))}
        </TableCell>
        <TableCell sx={{ verticalAlign: "top" }}>
          {computedDevices.map(item => (
            <EntityItem
              key={item.id}
              item={item}
              entityType="device"
              locationId={row.locationId}
              canEdit={canEditHealth}
              onOpenDialog={onOpenDialog}
              textColor={textColor}
              hoverColor={successColor}
              secondaryTextColor={textSecondaryColor}
              infoColor={infoColor}
            />
          ))}
        </TableCell>
        <TableCell sx={{ verticalAlign: "top" }}>
          {computedServices.map(item => (
            <EntityItem
              key={item.id}
              item={item}
              entityType="model"
              locationId={row.locationId}
              canEdit={canEditHealth}
              onOpenDialog={onOpenDialog}
              textColor={textColor}
              hoverColor={successColor}
              secondaryTextColor={textSecondaryColor}
              infoColor={infoColor}
            />
          ))}
        </TableCell>
      </TableRow>
    </>
  );
};

// Custom comparison to prevent re-renders when row data hasn't actually changed
const areRowPropsEqual = (
  prevProps: ITableHeadRowProps,
  nextProps: ITableHeadRowProps
): boolean => {
  const prev = prevProps.row;
  const next = nextProps.row;

  // Quick reference check first
  if (prev === next && prevProps.onOpenDialog === nextProps.onOpenDialog) {
    return true;
  }

  // Compare primitive fields
  if (
    prev.locationId !== next.locationId ||
    prev.locationName !== next.locationName ||
    prev.locationStatus !== next.locationStatus ||
    prev.nodeStatus !== next.nodeStatus ||
    prev.deviceStatus !== next.deviceStatus ||
    prev.serviceStatus !== next.serviceStatus
  ) {
    return false;
  }

  // Compare array lengths
  if (
    prev.nodes.length !== next.nodes.length ||
    prev.devices.length !== next.devices.length ||
    prev.services.length !== next.services.length
  ) {
    return false;
  }

  // Compare nodes by key fields that affect rendering
  for (let i = 0; i < prev.nodes.length; i++) {
    const pNode = prev.nodes[i];
    const nNode = next.nodes[i];

    if (
      pNode.nodeId !== nNode.nodeId ||
      pNode.status !== nNode.status ||
      pNode.isExpectedOnline !== nNode.isExpectedOnline ||
      pNode.reason !== nNode.reason ||
      pNode.assignedWatcher !== nNode.assignedWatcher
    ) {
      return false;
    }
  }

  // Compare devices
  for (let i = 0; i < prev.devices.length; i++) {
    const pDevice = prev.devices[i];
    const nDevice = next.devices[i];

    if (
      pDevice.deviceId !== nDevice.deviceId ||
      pDevice.status !== nDevice.status ||
      pDevice.isExpectedOnline !== nDevice.isExpectedOnline ||
      pDevice.reason !== nDevice.reason ||
      pDevice.assignedWatcher !== nDevice.assignedWatcher
    ) {
      return false;
    }
  }

  // Compare services
  for (let i = 0; i < prev.services.length; i++) {
    const pService = prev.services[i];
    const nService = next.services[i];

    if (
      pService.serviceId !== nService.serviceId ||
      pService.status !== nService.status ||
      pService.isExpectedOnline !== nService.isExpectedOnline ||
      pService.reason !== nService.reason ||
      pService.assignedWatcher !== nService.assignedWatcher
    ) {
      return false;
    }
  }

  return true;
};

const DeploymentHealthTableBodyRow = memo(
  DeploymentHealthTableBodyRowInner,
  areRowPropsEqual
);

DeploymentHealthTableBodyRow.displayName = "DeploymentHealthTableBodyRow";

export default DeploymentHealthTableBodyRow;
